import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";

import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { BULK_REMOVE_ASSET_IMAGES_MUTATION } from "../../../constants/graphql/mutations";

export default function BulkImageRemovalModal({ selectedIds = [], onClose, onComplete }) {
  const { notifySuccess, notifyError } = useNotification();
  const [confirmed, setConfirmed] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const [bulkRemoveImages] = useMutation(BULK_REMOVE_ASSET_IMAGES_MUTATION);

  const handleRemove = async () => {
    if (!confirmed || selectedIds.length === 0) return;

    setRemoving(true);
    setProgress({ current: 0, total: selectedIds.length });

    try {
      await bulkRemoveImages({
        variables: { assetIds: selectedIds },
        onSuccess: ({ successCount, failedAssetIds }) => {
          setProgress({ current: selectedIds.length, total: selectedIds.length });
          
          const result = {
            successCount: successCount || 0,
            failedCount: failedAssetIds?.length || 0,
            failedAssetIds: failedAssetIds || []
          };
          
          setResult(result);

          if (result.failedCount === 0) {
            notifySuccess(
              `Successfully removed all images from ${result.successCount} asset${result.successCount > 1 ? "s" : ""}`
            );
            setTimeout(() => {
              onComplete();
            }, 1500);
          } else if (result.successCount > 0) {
            notifySuccess(
              `Removed images from ${result.successCount} of ${selectedIds.length} assets`
            );
          } else {
            notifyError("Failed to remove images from any assets");
          }
        },
        onFailure: (errors) => {
          throw new Error(errors?.submit || "Failed to remove images");
        },
      });
    } catch (error) {
      console.error("Bulk image removal error:", error);
      notifyError("Failed to remove images. Please try again.");
      setRemoving(false);
      setProgress(null);
    }
  };

  const progressPercent =
    progress ? Math.round((progress.current / progress.total) * 100) : 0;

  const canRemove = confirmed && !removing;

  return (
    <Dialog open onClose={removing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6, position: "relative" }}>
        <Box display="flex" alignItems="center" justifyContent="center">
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          <Typography fontSize={22} fontWeight={500} color="text.primary">
            Remove All Images
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={removing}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" icon={false} sx={{ mb: 3, "& .MuiAlert-message": { width: "100%", textAlign: "center" } }}>
          This action will remove <strong>all images</strong> from the{" "}
          <strong>{selectedIds.length}</strong> selected item
          {selectedIds.length > 1 ? "s" : ""}.
        </Alert>

        {!removing && !result && (
          <Box display="flex" justifyContent="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={`I understand this action cannot be undone`}
            />
          </Box>
        )}

        {removing && progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progressPercent} />
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Processing {progress.current} / {progress.total} assets...
            </Typography>
          </Box>
        )}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={result.failedCount === 0 ? "success" : "warning"}>
              <Typography variant="body2">
                <strong>Successfully processed:</strong> {result.successCount} asset
                {result.successCount > 1 ? "s" : ""}
              </Typography>
              {result.failedCount > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Failed:</strong> {result.failedCount} asset
                  {result.failedCount > 1 ? "s" : ""}
                </Typography>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={removing}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleRemove}
            disabled={!canRemove}
          >
            {removing ? "Removing..." : "Remove All Images"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
